/*
  Phoneme features as types & collections
*/

export type Feature =
  // consonants
  | 'consonant'
  | PlaceOfArticulation
  | MannerOfArticulation
  | Voicing
  | 'pulmonic' | 'non-pulmonic'
  | 'click' | 'implosive' | 'ejective'
  | 'velarized' | 'ʃ and x'
  | 'laryngeal' // for PIE
  // vowels
  | 'vowel'
  | HeightOfTongue
  | PartOfTongue
  | Roundedness
  // diacritics
  | 'aspirated' | 'unaspirated' | 'tenuis' | 'unexploded' | 'syllabic' | 'non-syllabic' | 'velarized'
  | 'labialized' | 'palatalized' | 'rhotacized' | 'nasalized' | 'pharyngealized' | 'glottalized'
  | 'long' | 'half-long' | 'extra-short'
  | 'breathy-voiced' | 'breathy-voiced-aspirated' | 'creaky-voiced'
  | 'apical' | 'linguolabial' | 'laminal' | 'audibly-released' | 'not-audibly-released'
  | 'more-rounded' | 'less-rounded' | 'advanced' | 'retracted' | 'centralized' | 'mid-centralized'
  | 'raised' | 'lowered' | 'advanced-tongue-root' | 'retracted-tongue-root' | 'labial-spreading'
  | 'compression' | 'protrusion' | 'epenthetic'
  | 'emphatic' | 'vowel-coloring' | 'lateral-release' | 'labio-palatalization' | 'labio-dentalization'
  | 'egressive-airflow' | 'ingressive-airflow' | 'faucalized' | 'weak-articulation' | 'uralic' | 'whistled'
  | 'global-rise' | 'global-fall'
  // prosody
  | 'prosody'
  | 'primary-stress' | 'secondary-stress'
  | 'major-break' | 'minor-break' | 'morpheme-break' | 'syllable-break' | 'no-break'
  // tones
  | 'tone'
  | 'extra-high-tone' | 'high-tone' | 'mid-tone' | 'low-tone' | 'extra-low-tone'
  | 'rising' | 'falling' | 'high-rising' | 'low-rising' | 'high-falling' | 'low-falling' | 'rising-falling' | 'falling-rising'
  | 'upstep' | 'downstep'
  // grouping
  | 'group'
  | 'combine' | 'nocombine' | 'choice' | 'delimit' | 'start' | 'end'
  | 'abstract-phonemes' | 'detailed-phonemes' | 'precise-phonemes'
  | 'orthography' | 'morphophonemic-transcription' | 'prosodic-notation'
  | 'obscuring-noise' | 'indistinguishable-utterance'
  // misc
  | 'modifier' | 'length'
  ;

// consonant features
export type PlaceOfArticulation =
  | 'bilabial' | 'labiodental' | 'dental' | 'alveolar' | 'postalveolar'
  | 'retroflex' | 'alveolo-palatal' | 'palatal' | 'velar'
  | 'uvular' | 'pharyngeal' | 'epiglottal' | 'glottal' | 'uvular-epiglottal'
  | 'labial-alveolar' | 'labial-retroflex' | 'labial-palatal' | 'labial-velar' | 'labial-uvular';
;
export type MannerOfArticulation =
  | 'plosive' | 'nasal' | 'trill' | 'tap-flap' | 'lateral-flap' | 'affricate' | 'lateral-affricate'
  | 'fricative' | 'lateral-fricative' | 'approximant' | 'lateral-approximant' | 'lateral-click';
export type Voicing = 'voiceless' | 'voiced';

// vowel features
export type HeightOfTongue =  // also high (close), mid, low (open)
  | 'close' | 'near-close' | 'close-mid' | 'mid' | 'open-mid' | 'near-open' | 'open';
export type PartOfTongue =
  | 'front' | 'near-front' | 'central' | 'near-back' | 'back';
export type Roundedness = 'unrounded' | 'rounded';


//
// as collections

export const placeOfArticulation: PlaceOfArticulation[] = [
  'bilabial', 'labiodental', 'dental', 'alveolar', 'postalveolar',
  'retroflex', 'alveolo-palatal', 'palatal', 'velar',
  'uvular', 'pharyngeal', 'epiglottal', 'glottal', 'uvular-epiglottal',
  'labial-alveolar', 'labial-retroflex', 'labial-palatal', 'labial-velar', 'labial-uvular'
];

export const mannerOfArticulation: MannerOfArticulation[] = [
  'plosive', 'nasal', 'trill', 'tap-flap', 'lateral-flap', 'affricate', 'lateral-affricate',
  'fricative', 'lateral-fricative', 'approximant', 'lateral-approximant'
];

export const voicing: Voicing[] = [
  'voiceless', 'voiced'
];

export const heightOfTongue: HeightOfTongue[] = [
  'close', 'near-close', 'close-mid', 'mid', 'open-mid', 'near-open', 'open'
];

export const partOfTongue: PartOfTongue[] = [
  'front', 'near-front', 'central', 'near-back', 'back'
];

export const roundedness: Roundedness[] = [
  'unrounded', 'rounded',
];

//
// misc features as collections

export const basicFeatures: Feature[] = ['consonant', 'vowel', 'tone'];

export const miscConsonantFeatures: Feature[] = [
  'pulmonic', 'non-pulmonic',
  'click', 'implosive', 'ejective',
  'velarized', 'ʃ and x',
  'laryngeal',
];

export const diacriticFeatures: Feature[] = [
  'aspirated', 'unaspirated', 'unexploded', 'syllabic', 'non-syllabic', 'velarized',
  'labialized', 'palatalized', 'rhotacized', 'nasalized', 'pharyngealized', 'glottalized',
  'long', 'half-long', 'extra-short',
  'breathy-voiced', 'breathy-voiced-aspirated', 'creaky-voiced',
  'apical', 'linguolabial', 'laminal', 'not-audibly-released',
  'more-rounded', 'less-rounded', 'advanced', 'retracted', 'centralized', 'mid-centralized',
  'raised', 'lowered', 'advanced-tongue-root', 'retracted-tongue-root', 'labial-spreading',
  'compression', 'protrusion',
  'emphatic', 'vowel-coloring', 'lateral-release', 'labio-palatalization', 'labio-dentalization',
  'egressive-airflow', 'ingressive-airflow', 'faucalized', 'weak-articulation', 'uralic', 'whistled',
  'global-rise', 'global-fall',
];

export const toneFeatures: Feature[] = [
  'extra-high-tone', 'high-tone', 'mid-tone', 'low-tone', 'extra-low-tone',
  'rising', 'falling', 'high-rising', 'low-rising', 'rising-falling',
  'upstep', 'downstep',
];

export const prosodyFeatures: Feature[] = [
  'primary-stress', 'secondary-stress',
  'major-break', 'minor-break', 'morpheme-break', 'syllable-break', 'no-break',
];

export const groupFeatures: Feature[] = [
  'group',
  'combine', 'nocombine', 'choice', 'delimit', 'start', 'end',
  'abstract-phonemes', 'detailed-phonemes', 'precise-phonemes',
  'orthography', 'morphophonemic-transcription', 'prosodic-notation',
  'obscuring-noise', 'indistinguishable-utterance',
];

export const miscFeatures: Feature[] = [
  'length'
]

/** all phoneme features in an array */
export function getFeatureArray(): Feature[] {
  if (allFeatures.length === 0) {
    allFeatures =
      basicFeatures
        .concat(placeOfArticulation)
        .concat(mannerOfArticulation)
        .concat(voicing)
        .concat(heightOfTongue)
        .concat(partOfTongue)
        .concat(roundedness)
        .concat(miscConsonantFeatures)
        .concat(diacriticFeatures)
        .concat(toneFeatures)
        .concat(prosodyFeatures)
        .concat(groupFeatures)
        .concat(miscFeatures)
      ;
  }
  return allFeatures;
}
let allFeatures: Feature[] = [];

/** all phoneme features in a set */
export function getFeatureSet(): Set<Feature> {
  if (allFeatureSet.size === 0) {
    const list = getFeatureArray();
    list.forEach(f => allFeatureSet?.add(f));
  }
  return allFeatureSet;
}
let allFeatureSet: Set<Feature> = new Set();;
